/**
 * Owner: Masa Bayu
 * File: natural-reader-prefs.ts
 * Date:Time: 2026-10-17 19:20
 * Description: QX Natural Reader 03 — tetapan paparan GLOBAL (theme, font scale,
 *   line spacing, font family, auto page turn, night mode). Persist localStorage;
 *   corrupt-safe; nilai di-clamp dalam julat disahkan.
 */

export type NaturalTheme = 'light' | 'sepia' | 'dark';
export type NaturalFontFamily = 'serif' | 'sans';

export interface NaturalReaderPrefs {
  readonly theme: NaturalTheme;
  readonly fontScale: number;
  readonly lineSpacing: number;
  readonly fontFamily: NaturalFontFamily;
  readonly autoPageTurn: boolean;
  readonly nightMode: boolean;
}

export const NATURAL_PREFS_STORAGE_KEY = 'qxread.natural-reader-prefs.v1';
export const NATURAL_FONT_SCALE_MIN = 0.8;
export const NATURAL_FONT_SCALE_MAX = 1.6;
export const NATURAL_LINE_SPACING_MIN = 1.2;
export const NATURAL_LINE_SPACING_MAX = 2.2;
export const NATURAL_AUTO_PAGE_TURN_MS = 8000;

export const NATURAL_PREFS_DEFAULTS: NaturalReaderPrefs = Object.freeze({
  theme: 'light',
  fontScale: 1,
  lineSpacing: 1.7,
  fontFamily: 'serif',
  autoPageTurn: false,
  nightMode: false,
});

export function clampNaturalFontScale(value: number): number {
  if (!Number.isFinite(value)) {
    return NATURAL_PREFS_DEFAULTS.fontScale;
  }
  return Math.min(NATURAL_FONT_SCALE_MAX, Math.max(NATURAL_FONT_SCALE_MIN, value));
}

export function clampNaturalLineSpacing(value: number): number {
  if (!Number.isFinite(value)) {
    return NATURAL_PREFS_DEFAULTS.lineSpacing;
  }
  return Math.min(NATURAL_LINE_SPACING_MAX, Math.max(NATURAL_LINE_SPACING_MIN, value));
}

function sanitize(input: unknown): NaturalReaderPrefs {
  if (typeof input !== 'object' || input === null) {
    return { ...NATURAL_PREFS_DEFAULTS };
  }
  const record = input as Record<string, unknown>;
  const theme: NaturalTheme =
    record.theme === 'sepia' || record.theme === 'dark' ? record.theme : 'light';
  const fontFamily: NaturalFontFamily = record.fontFamily === 'sans' ? 'sans' : 'serif';
  return {
    theme,
    fontScale:
      typeof record.fontScale === 'number'
        ? clampNaturalFontScale(record.fontScale)
        : NATURAL_PREFS_DEFAULTS.fontScale,
    lineSpacing:
      typeof record.lineSpacing === 'number'
        ? clampNaturalLineSpacing(record.lineSpacing)
        : NATURAL_PREFS_DEFAULTS.lineSpacing,
    fontFamily,
    autoPageTurn:
      typeof record.autoPageTurn === 'boolean'
        ? record.autoPageTurn
        : NATURAL_PREFS_DEFAULTS.autoPageTurn,
    nightMode:
      typeof record.nightMode === 'boolean'
        ? record.nightMode
        : NATURAL_PREFS_DEFAULTS.nightMode,
  };
}

export function getNaturalReaderPrefs(): NaturalReaderPrefs {
  try {
    const raw = window.localStorage.getItem(NATURAL_PREFS_STORAGE_KEY);
    if (raw === null) {
      return { ...NATURAL_PREFS_DEFAULTS };
    }
    return sanitize(JSON.parse(raw));
  } catch {
    return { ...NATURAL_PREFS_DEFAULTS };
  }
}

export function setNaturalReaderPrefs(patch: Partial<NaturalReaderPrefs>): NaturalReaderPrefs {
  const current = getNaturalReaderPrefs();
  const next: NaturalReaderPrefs = sanitize({ ...current, ...patch });
  try {
    window.localStorage.setItem(NATURAL_PREFS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage penuh/ditolak — kekal dalam memori sesi ini.
  }
  return next;
}

export function resetNaturalReaderPrefs(): NaturalReaderPrefs {
  try {
    window.localStorage.removeItem(NATURAL_PREFS_STORAGE_KEY);
  } catch {
    // Abaikan — kekal dalam memori sesi ini.
  }
  return { ...NATURAL_PREFS_DEFAULTS };
}

/** Tema berkesan — Night Mode memaksa gelap. */
export function resolveEffectiveNaturalTheme(prefs: NaturalReaderPrefs): NaturalTheme {
  return prefs.nightMode ? 'dark' : prefs.theme;
}
